'use client'

import { Button } from '@/components/ui/button'
import imageCompression from 'browser-image-compression'
import Image from 'next/image'
import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'

interface ImageUploaderProps {
  currentImageUrl: string | undefined
  onImageUpdated: (url: string) => void
  onUpload: (file: File, onProgress?: (progress: number) => void) => Promise<string>
  onDelete?: () => Promise<void>
  canEdit?: boolean
  imageAlt: string
  width?: number
  height?: number
  className?: string
  changeButtonText?: string
  deleteButtonText?: string
  successMessage?: string
  deleteSuccessMessage?: string
  errorMessage?: string
  deleteErrorMessage?: string
}

export function ImageUploader({
  currentImageUrl,
  onImageUpdated,
  onUpload,
  onDelete,
  canEdit = false,
  imageAlt,
  width = 80,
  height = 80,
  className = 'object-cover rounded-full',
  changeButtonText = 'Change',
  deleteButtonText = 'Delete',
  successMessage = 'Image updated successfully',
  deleteSuccessMessage = 'Image removed successfully',
  errorMessage = 'Failed to update image',
  deleteErrorMessage = 'Failed to remove image',
}: ImageUploaderProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [compressionProgress, setCompressionProgress] = useState(0)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [processingStage, setProcessingStage] = useState<'compressing' | 'uploading' | 'idle'>('idle')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Handler for when file is selected via input
  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (!file) return

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB')
        return
      }

      setIsUploading(true)
      setCompressionProgress(0)
      setUploadProgress(0)
      setProcessingStage('compressing')

      try {
        // Get file extension
        const fileExtension = file.name.split('.').pop()?.toLowerCase() || ''

        // Compress the image before uploading with optimizations based on image type
        const options = {
          maxSizeMB: 0.5, // Max size after compression (0.5MB for most images)
          maxWidthOrHeight: 800, // Resize large images to 800px max dimension
          useWebWorker: true,
          // Adjust quality based on image type
          initialQuality: file.type.includes('png') ? 0.8 : 0.7, // Higher quality for PNG
          alwaysKeepResolution: false, // Allow resizing for better compression
          // Extra options for better quality retention
          fileType: file.type, // Preserve original file type
          // Show compression progress
          onProgress: (progress: number) => {
            setCompressionProgress(progress)
          },
        }

        // Skip compression for small images that are already optimized
        let compressedFile: File

        if (file.size < 200 * 1024 && (fileExtension === 'svg' || fileExtension === 'webp')) {
          compressedFile = file
          // Just to show progress
          setCompressionProgress(100)
        } else {
          // Compress the image
          compressedFile = await imageCompression(file, options)
        }

        // Upload compressed image
        setProcessingStage('uploading')
        const imageUrl = await onUpload(compressedFile, setUploadProgress)

        // Notify parent component
        onImageUpdated(imageUrl)

        toast.success(successMessage)
      } catch (error) {
        console.error('Error uploading image:', error)
        toast.error(errorMessage)
      } finally {
        setIsUploading(false)
        setCompressionProgress(0)
        setUploadProgress(0)
        setProcessingStage('idle')
      }
    },
    [onUpload, onImageUpdated, successMessage, errorMessage],
  )

  // Handler for image deletion
  const handleDeleteImage = useCallback(async () => {
    if (!currentImageUrl || !onDelete) return

    setIsUploading(true)

    try {
      await onDelete()
      onImageUpdated('')
      toast.success(deleteSuccessMessage)
    } catch (error) {
      console.error('Error removing image:', error)
      toast.error(deleteErrorMessage)
    } finally {
      setIsUploading(false)
    }
  }, [currentImageUrl, onDelete, onImageUpdated, deleteSuccessMessage, deleteErrorMessage])

  return (
    <div className='flex items-center gap-5 w-full'>
      <div className='relative'>
        <Image
          width={width}
          height={height}
          alt={imageAlt}
          src={currentImageUrl || '/images/avatar.jpeg'}
          className={`${className} h-20 w-20`}
        />

        {isUploading && (
          <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full'>
            {processingStage === 'compressing' && compressionProgress > 0 ? (
              <div className='text-xs text-white font-medium'>
                <div>{Math.round(compressionProgress)}%</div>
                <div className='text-[10px]'>compressing</div>
              </div>
            ) : processingStage === 'uploading' && uploadProgress > 0 ? (
              <div className='text-xs text-white font-medium'>
                <div>{Math.round(uploadProgress)}%</div>
                <div className='text-[10px]'>uploading</div>
              </div>
            ) : (
              <div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
            )}
          </div>
        )}
      </div>

      {canEdit && (
        <div className='flex gap-4 flex-wrap'>
          <div>
            <input
              type='file'
              accept='image/*'
              className='hidden'
              onChange={handleFileChange}
              disabled={isUploading}
              ref={fileInputRef}
            />
            <Button
              variant='outline'
              className='w-fit cursor-pointer'
              disabled={isUploading}
              type='button'
              onClick={() => fileInputRef.current?.click()}
            >
              {changeButtonText}
            </Button>
          </div>

          {currentImageUrl && onDelete && (
            <Button
              variant='outline'
              skin='destructive'
              className='w-fit'
              onClick={handleDeleteImage}
              disabled={isUploading}
              type='button'
            >
              {deleteButtonText}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
